import type {CSSProperties} from 'react';
import {WEDGE_BRIEF_RECOVERY} from '@territory/tokens';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefRecoveryLook = 'recovery-confirm' | 'recovery-cta-amber' | 'recovery-cta-red' | 'recovery-chip';

const BRIEF_RECOVERY_LOOKS = new Set<string>(['recovery-confirm', 'recovery-cta-amber', 'recovery-cta-red', 'recovery-chip']);
export const isWedgeBriefRecoveryLook = (look: string | undefined): look is WedgeBriefRecoveryLook => look !== undefined && BRIEF_RECOVERY_LOOKS.has(look);

/* Released Brief recovery panels (the unconfirmed account and the failed research). The confirm is the full width red action of the
   unconfirmed card. The two primary actions are the retry of the failed card, amber for a recoverable failure and red for a hard one:
   a 48px flex box with white ink, which dims to 78% and shows the wait cursor while the retry runs (`disabled`). The chip is one recovery
   step under it: a full width left aligned row on the elevated fill. Each keeps the declaration order of its source and reads the exact
   legacy roles. The white ink and the press highlights are the fixed `WEDGE_BRIEF_RECOVERY` roles. None draws a transparent target or an
   anchor (wedgeSettingsDrawsHit): all four are at least 38px tall and the chips stack 6px apart, so a region would reach the next row. */
export function wedgeBriefRecoveryStyle(look: WedgeBriefRecoveryLook, disabled: boolean): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'recovery-confirm': return {
      width: '100%', padding: '11px', borderRadius: 11,
      background: r('red'), color: WEDGE_BRIEF_RECOVERY.ink, border: 0, cursor: 'pointer',
      fontSize: 13.5, fontWeight: 700, fontFamily: r('font'),
    };
    case 'recovery-cta-amber':
    case 'recovery-cta-red': return {
      width: '100%', padding: '14px', borderRadius: 11,
      background: look === 'recovery-cta-amber' ? r('amber') : r('red'), color: WEDGE_BRIEF_RECOVERY.ink, border: 0,
      cursor: disabled ? 'wait' : 'pointer',
      fontSize: 14.5, fontWeight: 700, fontFamily: r('font'),
      minHeight: 48,
      touchAction: 'manipulation',
      WebkitTapHighlightColor: WEDGE_BRIEF_RECOVERY.tapCta,
      opacity: disabled ? 0.78 : 1,
      transition: `opacity 200ms ${r('ease')}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    };
    case 'recovery-chip': return {
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '12px 14px', borderRadius: 9,
      background: r('bgElev'),
      border: `1px solid ${r('cardBorder')}`,
      fontSize: 13, color: r('label'),
      fontFamily: r('font'),
      width: '100%', textAlign: 'left', cursor: 'pointer',
      minHeight: 48, touchAction: 'manipulation',
      WebkitTapHighlightColor: WEDGE_BRIEF_RECOVERY.tapChip,
    };
  }
}
